import { PrintQueue, togglePrintQueue, deliveryKey } from './packages/PrintQueue';
import { useState, useCallback } from 'react';
import { ArrowLeft, Package, BarChart3, Archive } from 'lucide-react';
import { usePackageDashboard } from './shared/usePackageDashboard';
import { GlobalSearchBar } from './shared/GlobalSearchBar';
import { DashboardOverview } from './overview/DashboardOverview';
import { PackageTable } from './packages/PackageTable';
import { InventoryView } from './inventory/InventoryView';

/**
 * PackageDashboard — Shell component
 *
 * Owns the top-level navigation state (view) and wires the
 * shared data hook to each child view. No data fetching here —
 * that lives in usePackageDashboard.
 *
 * Views:
 *   'overview'   — KPI cards, stuck alerts, inventory health strip
 *   'packages'   — Full filterable package table
 *   'inventory'  — Stock levels and low-stock highlights
 */
export function PackageDashboard({ setActiveComponent, userId }) {
  // Which of the three views is active
  const [view, setView] = useState('overview');

  // When drilling down from a status card, pre-apply this filter
  const [presetFilter, setPresetFilter] = useState(null);

  // Which package has its history panel open (null = closed)
  const [historyPackageId, setHistoryPackageId] = useState(null);

  // Whether the scanner modal is open
  const [scannerOpen, setScannerOpen] = useState(false);

  // Shared data hook — passed down to children as needed
  const dashboardData = usePackageDashboard(view);
  const [printQueue, setPrintQueue] = useState([]);
  const [immediatePrint, setImmediatePrint] = useState(null);

  // ── Navigation helpers ────────────────────────────────────────────────────

  /**
   * Navigate to the package table, optionally pre-filtering by status.
   * Called when the admin clicks a KPI status card.
   */
  const goToPackages = useCallback((statusFilter = null) => {
    if (statusFilter) {
      setPresetFilter({ status: statusFilter, delivery_date: '' });
    } else {
      setPresetFilter(null);
    }
    setView('packages');
  }, []);

  const goToInventory = useCallback(() => {
    setView('inventory');
  }, []);

  const goToOverview = useCallback(() => {
    setView('overview');
    setPresetFilter(null);
    setHistoryPackageId(null);
  }, []);

  // ── View label for the breadcrumb ─────────────────────────────────────────
  const viewLabel = {
    overview: 'Resumen',
    packages: 'Paquetes',
    inventory: 'Inventario',
  }[view];

  return (
    <div className="min-h-screen min-w-0 bg-gray-50 flex flex-col">

      {/* ── Fixed Header ──────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-3">

          {/* Back to admin menu */}
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-brand-primary transition-colors shrink-0"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Title + breadcrumb */}
          <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-none">
            <Package size={22} className="text-brand-primary" />
            <span className="font-montserrat font-extrabold text-brand-primary text-base sm:text-lg leading-none">
              Bodega
            </span>
            {view !== 'overview' && (
              <>
                <span className="text-gray-300 font-light">/</span>
                <button
                  onClick={goToOverview}
                  className="hidden sm:inline text-gray-400 hover:text-brand-primary font-montserrat text-sm transition-colors"
                >
                  Resumen
                </button>
                <span className="hidden sm:inline text-gray-300 font-light">/</span>
                <span className="truncate font-montserrat font-semibold text-gray-700 text-sm">
                  {viewLabel}
                </span>
              </>
            )}
          </div>

          {/* Global search — grows to fill available space */}
          <div className="order-3 w-full min-w-0 lg:order-none lg:w-auto lg:flex-1 lg:max-w-xl">
            <GlobalSearchBar
              onSearch={dashboardData.searchPackages}
              searchResults={dashboardData.searchResults}
              searchLoading={dashboardData.searchLoading}
              searchQuery={dashboardData.searchQuery}
              setSearchQuery={dashboardData.setSearchQuery}
              onSelectPackage={(pkg) => {
                // Navigate to packages view and open history for selected package
                setView('packages');
                setHistoryPackageId(pkg.id);
              }}
            />
          </div>

          {/* View switcher tabs */}
          <nav className="hidden lg:flex items-center gap-1 shrink-0">
            <NavTab
              active={view === 'overview'}
              onClick={goToOverview}
              icon={<BarChart3 size={16} />}
              label="Resumen"
            />
            <NavTab
              active={view === 'packages'}
              onClick={() => goToPackages()}
              icon={<Package size={16} />}
              label="Paquetes"
            />
            <NavTab
              active={view === 'inventory'}
              onClick={goToInventory}
              icon={<Archive size={16} />}
              label="Inventario"
            />
          </nav>
        </div>

        {/* Mobile tab bar — shown below search on small screens */}
        <div className="flex lg:hidden border-t border-gray-100">
          <MobileTab
            active={view === 'overview'}
            onClick={goToOverview}
            icon={<BarChart3 size={18} />}
            label="Resumen"
          />
          <MobileTab
            active={view === 'packages'}
            onClick={() => goToPackages()}
            icon={<Package size={18} />}
            label="Paquetes"
          />
          <MobileTab
            active={view === 'inventory'}
            onClick={goToInventory}
            icon={<Archive size={18} />}
            label="Inventario"
          />
        </div>
      </header>

      {/* ── View Content ──────────────────────────────────────────────────── */}
      <PrintQueue queue={printQueue} setQueue={setPrintQueue} immediate={immediatePrint} onImmediateHandled={() => setImmediatePrint(null)} />
      <main className="min-w-0 flex-1 overflow-hidden">
        {view === 'overview' && (
          <DashboardOverview
            dashboardData={dashboardData}
            onStatusCardClick={goToPackages}
            onInventoryClick={goToInventory}
            onPackageHistoryClick={(id) => {
              setHistoryPackageId(id);
              setView('packages');
            }}
          />
        )}

        {view === 'packages' && (
          <PackageTable
            dashboardData={dashboardData}
            presetFilter={presetFilter}
            onPrint={pkg => setImmediatePrint({ ...pkg })}
            onQueue={pkg => setPrintQueue(prev => togglePrintQueue(prev, pkg))}
            isQueued={pkg => printQueue.some(p => deliveryKey(p) === deliveryKey(pkg))}
            historyPackageId={historyPackageId}
            setHistoryPackageId={setHistoryPackageId}
            scannerOpen={scannerOpen}
            setScannerOpen={setScannerOpen}
            userId={userId}
          />
        )}

        {view === 'inventory' && (
          <InventoryView
            dashboardData={dashboardData}
            userId={userId}
          />
        )}
      </main>
    </div>
  );
}

// ── Small sub-components for the nav tabs ─────────────────────────────────

function NavTab({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-montserrat font-semibold transition-all ${
        active
          ? 'bg-brand-primary text-white'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function MobileTab({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-xs font-montserrat font-semibold transition-colors ${
        active
          ? 'text-brand-primary border-b-2 border-brand-primary'
          : 'text-gray-400'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
